/**
 * This file contains the logic for calculating the grocery budget for a person
 * based on their age, plan, and gender. The data for this comes from the USDA
 * https://www.fns.usda.gov/cnpp/usda-food-plans-cost-food-reports-monthly-reports
 *
 * To get then numbers below, I used the following steps:
 * 1. Take the weekly numbers for each age group and divided by 7 to get the daily numbers.
 * 2. Take the daily numbers and turn the amount into cents
 *  *
 * The data was last updated to October 2024 data sets, from here: https://www.fns.usda.gov/cnpp/usda-food-plans-cost-food-monthly-reports
 */

// NOTE: I went ahead and updated each value in the indexes below to do the division inline so the numbers are easier to update in the future.

const roundToTenth = (num) => Math.round(num / 10) * 10

// Use age ranges to find the correct category to get the budget
const ageRanges = [
  {ageRange: [0, 1], category: 10},
  {ageRange: [2, 3], category: 20},
  {ageRange: [4, 5], category: 30},
  {ageRange: [6, 8], category: 40},
  {ageRange: [9, 11], category: 50},
  {ageRange: [12, 13], category: 60},
  {ageRange: [14, 18], category: 70},
  {ageRange: [19, 50], category: 80},
  {ageRange: [51, 70], category: 90}
]

// Daily Budget levels in cents (1 cent = 0.01)
const budgetLevels = {
  thrifty: {
    child: {
      10: 2550 / 7,
      20: 3840 / 7,
      30: 4180 / 7,
      40: 4650 / 7,
      50: 5380 / 7
    },
    male: {
      60: 5750 / 7,
      70: 7250 / 7,
      80: 7080 / 7,
      90: 6260 / 7,
      100: 5970 / 7
    },
    female: {
      60: 4980 / 7,
      70: 5760 / 7,
      80: 5660 / 7,
      90: 5260 / 7,
      100: 5780 / 7
    }
  },
  low: {
    child: {
      10: 3630 / 7,
      20: 3790 / 7,
      30: 3950 / 7,
      40: 5650 / 7,
      50: 5980 / 7
    },
    male: {
      60: 6970 / 7,
      70: 7130 / 7,
      80: 7030 / 7,
      90: 6600 / 7,
      100: 6550 / 7
    },
    female: {
      60: 5950 / 7,
      70: 5980 / 7,
      80: 6100 / 7,
      90: 5940 / 7,
      100: 5930 / 7
    }
  },
  moderate: {
    child: {
      10: 4100 / 7,
      20: 4560 / 7,
      30: 4850 / 7,
      40: 6680 / 7,
      50: 7660 / 7
    },
    male: {
      60: 8640 / 7,
      70: 8880 / 7,
      80: 8810 / 7,
      90: 8260 / 7,
      100: 8060 / 7
    },
    female: {
      60: 7060 / 7,
      70: 7120 / 7,
      80: 7430 / 7,
      90: 7320 / 7,
      100: 7280 / 7
    }
  },
  liberal: {
    child: {
      10: 4990 / 7,
      20: 5550 / 7,
      30: 5900 / 7,
      40: 7820 / 7,
      50: 8950 / 7
    },
    male: {
      60: 10120 / 7,
      70: 10350 / 7,
      80: 10710 / 7,
      90: 9870 / 7,
      100: 9860 / 7
    },
    female: {
      60: 8790 / 7,
      70: 8820 / 7,
      80: 9480 / 7,
      90: 8770 / 7,
      100: 8690 / 7
    }
  }
}

// for the grocery budget calculator we are not asking for sex, so I used an "adult" section instead of male/female
// within those, it averages the male / female weekly values for each age group
// then divides by 7 as before, to get the daily value
const genderlessBudgetLevels = {
  thrifty: {
    child: {
      10: 2550 / 7,
      20: 3840 / 7,
      30: 4180 / 7,
      40: 4650 / 7,
      50: 5380 / 7
    },
    adult: {
      60: (5750 + 4980) / 2 / 7,
      70: (7250 + 5760) / 2 / 7,
      80: (7080 + 5660) / 2 / 7,
      90: (6260 + 5260) / 2 / 7,
      100: (5970 + 5780) / 2 / 7
    }
  },
  low: {
    child: {
      10: 3630 / 7,
      20: 3790 / 7,
      30: 3950 / 7,
      40: 5650 / 7,
      50: 5980 / 7
    },
    adult: {
      60: (6970 + 5950) / 2 / 7,
      70: (7130 + 5980) / 2 / 7,
      80: (7030 + 6100) / 2 / 7,
      90: (6600 + 5940) / 2 / 7,
      100: (6550 + 5930) / 2 / 7
    }
  },
  moderate: {
    child: {
      10: 4100 / 7,
      20: 4560 / 7,
      30: 4850 / 7,
      40: 6680 / 7,
      50: 7660 / 7
    },
    adult: {
      60: (8640 + 7060) / 2 / 7,
      70: (8880 + 7120) / 2 / 7,
      80: (8810 + 7430) / 2 / 7,
      90: (8260 + 7320) / 2 / 7,
      100: (8060 + 7280) / 2 / 7
    }
  },
  liberal: {
    child: {
      10: 4990 / 7,
      20: 5550 / 7,
      30: 5900 / 7,
      40: 7820 / 7,
      50: 8950 / 7
    },
    adult: {
      60: (10120 + 8790) / 2 / 7,
      70: (10350 + 8820) / 2 / 7,
      80: (10710 + 9480) / 2 / 7,
      90: (9870 + 8770) / 2 / 7,
      100: (9860 + 8690) / 2 / 7
    }
  }
}

export const getAgeCategory = (age) => {
  const match = ageRanges.find((range) => age >= range.ageRange[0] && age <= range.ageRange[1])
  return match ? match.category : 100
}

export const getBudget = {
  daily: ({age, plan, gender}) => roundToTenth(getBudgetForPerson({age, plan, gender}), 10),
  weekly: ({age, plan, gender}) => roundToTenth(getBudgetForPerson({age, plan, gender}) * 7, 10),
  monthly: ({age, plan, gender}) => roundToTenth(getBudgetForPerson({age, plan, gender}) * 7 * 4.333, 10)
}

/**
 * Get the daily budget for a person in cents
 * @param age integer age of the person
 * @param planLevel low, moderate, liberal
 * @param gender male or female
 * @returns a dollar amount in cents
 */

export const getBudgetForPerson = ({age, plan, gender}) => {
  const ageCategory = getAgeCategory(age)
  if (gender) {
    // We don't care about the gender of a child
    const category = age <= 11 ? "child" : gender
    return budgetLevels[plan][category][ageCategory]
  } else {
    const category = age <= 11 ? "child" : "adult"
    return genderlessBudgetLevels[plan][category][ageCategory]
  }
}

/**
 * Expecting an incoming object with the following structure
 * {
 *  members: [
 *    {age: 10, gender: 'male'},
 *    {age: 10, gender: 'female'},
 *    {age 15, gender: 'male'},
 *    {age: 45, gender: 'female'},
 *    {age: 42, gender: 'male'}
 * ],
 * plan: 'moderate'
 * timePeriod: 'monthly'
 * }
 */

// NOTE: In the case of the grocery budget calculator, data does not include gender.
// this is handled in getBudgetForPerson, above

export const calculateFamilyBudget = ({members, plan, timePeriod}) => {
  let totalBudget = 0
  members.forEach(({age, gender}) => {
    const budget = getBudget[timePeriod]({age, plan, gender})
    totalBudget += budget
  })

  /**
   * Following logic is from the pdf file to calculate the total budget for a family
   * The costs given are for individuals in 4-person households.
   * For individuals in other size households, the following adjustments are suggested:
   * 1-person—add 20 percent;
   * 2-person—add 10 percent;
   * 3-person—add 5 percent;
   * 4-person—no adjustment;
   * 5- or 6- person—subtract 5 percent;
   * 7- (or more) person—subtract 10 percent.
   * To calculate total foods costs for a household,
   * (1) sum the food costs for each individual in the household and then
   * (2) apply the appropriate adjustment factor based on household size.
   */

  switch (members.length) {
    case 1:
      totalBudget *= 1.2
      break
    case 2:
      totalBudget *= 1.1
      break
    case 3:
      totalBudget *= 1.05
      break
    case 4:
      break
    case 5:
    case 6:
      totalBudget *= 0.95
      break
    case 7:
    default:
      totalBudget *= 0.9
      break
  }
  return parseFloat((Math.round(roundToTenth(totalBudget)) / 100).toFixed(2))
}

export const getGroceryBudgetAllLevels = (familyPlan) => {
  const planLevels = ["thrifty", "low", "moderate", "liberal"]
  const results = {}
  planLevels.forEach((plan) => {
    results[plan] = calculateFamilyBudget({...familyPlan, plan})
  })
  return results
}

export const getGroceryBudget = (familyPlan) => {
  if (familyPlan.plan === "all") {
    return getGroceryBudgetAllLevels(familyPlan)
  }
  return calculateFamilyBudget(familyPlan)
}
